// The registration history of a company, for the timeline on the company page.
//
// Each CVR value has a period { gyldigFra, gyldigTil }. A change is where a period
// starts or ends. CVR often splits one value into periods in a row, when a new
// registration changes something else. Periods in a row with the same value are
// one period here.
//
// An owner share (EJERANDEL_PROCENT) in the CVR owner register is the low end of a
// range: 0.1 means 10–14,99 %.
//
// CVR does not hold the money of a capital increase. The annual report of that fiscal
// year does, but each filer writes it in its own way (see `investment`). The report
// gives the sum of the year, not of one payment, so the page calls the amount an estimate.

import { formatAddress } from './entity.js';
import { companyName, kindOf, participantCvr } from './relations.js';
import { thousands } from './format.js';

const SHARE_LIMITS = [0.05, 0.1, 0.15, 0.2, 0.25, 0.3333, 0.5, 0.6667, 0.9, 1];
const OWNER_REGISTER = 'EJERREGISTER';
const KIND_ORDER = ['merger', 'founded', 'name', 'form', 'capital', 'owner', 'role', 'status', 'address', 'industry', 'articles'];
const ROLE_NAMES = { REVISION: 'revisor', STIFTERE: 'stifter' };
const FSA_NAMESPACE = 'http://xbrl.dcca.dk/fsa';
const INSTANCE_NAMESPACE = 'http://www.xbrl.org/2003/instance';

function day(value) {
  return value ? String(value).slice(0, 10) : null;
}

function addDays(date, days) {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function daysBetween(a, b) {
  return Math.abs(Date.parse(`${a}T00:00:00Z`) - Date.parse(`${b}T00:00:00Z`)) / 86400000;
}

function list(value) {
  if (value === null || value === undefined) return [];
  return Array.isArray(value) ? value : [value];
}

function percent(fraction) {
  return String(Math.round(fraction * 10000) / 100).replace('.', ',');
}

function kroner(value) {
  return `${thousands(value)} kr.`;
}

/** An owner share of the CVR register as its range: 0.1 -> "10–14,99 %". */
export function shareRange(value) {
  const share = Number(value);
  const index = SHARE_LIMITS.findIndex((limit) => Math.abs(limit - share) < 1e-6);
  if (index === -1) return `${percent(share)} %`;
  if (index === SHARE_LIMITS.length - 1) return '100 %';
  return `${percent(SHARE_LIMITS[index])}–${percent(SHARE_LIMITS[index + 1] - 0.0001)} %`;
}

// The periods of a value list, oldest first. Periods in a row with the same value join.
function periodsOf(values, valueOf) {
  const periods = list(values)
    .map((v) => ({ value: valueOf(v), from: day(v?.periode?.gyldigFra), to: day(v?.periode?.gyldigTil) }))
    .filter((p) => p.from && p.value !== null && p.value !== undefined && p.value !== '')
    .sort((a, b) => a.from.localeCompare(b.from));
  const joined = [];
  for (const period of periods) {
    const last = joined[joined.length - 1];
    if (last && last.value === period.value && (!last.to || period.from <= addDays(last.to, 1))) {
      if (last.to && (!period.to || period.to > last.to)) last.to = period.to;
      continue;
    }
    joined.push({ ...period });
  }
  return joined;
}

// Each start and end of the periods: { date, before, after }. `before` is the value of
// the period just before, and `after` is null when nothing follows an end.
function changesOf(periods) {
  const changes = [];
  periods.forEach((period, i) => {
    const prev = periods[i - 1];
    const next = periods[i + 1];
    const follows = prev?.to && period.from <= addDays(prev.to, 1);
    changes.push({ date: period.from, before: follows ? prev.value : null, after: period.value });
    if (period.to && !(next && next.from <= addDays(period.to, 1))) {
      changes.push({ date: period.to, before: period.value, after: null });
    }
  });
  return changes;
}

function roleName(value) {
  return ROLE_NAMES[value] ?? String(value).toLocaleLowerCase('da');
}

// A MERGER. `fusioner` holds only the number of a CVR merger unit, not the other
// company. Each company in the same merger has the same unit number. A company with
// `udgaaende` values continues after the merger; a company with only `indgaaende`
// values goes into the other company. This rule comes from the records of RPA House
// ApS and Droids Agency ApS (2022-07-19).

/** The mergers of a company: `[{ unit, date, continues }]`. */
export function mergersOf(company) {
  const mergers = [];
  for (const fusion of list(company?.fusioner)) {
    const unit = Number(fusion?.enhedsNummerOrganisation);
    if (!Number.isFinite(unit)) continue;
    const values = (key) => list(fusion[key]).flatMap((a) => list(a?.vaerdier));
    const dates = [...values('indgaaende'), ...values('udgaaende'), ...list(fusion.organisationsNavn)]
      .map((v) => day(v?.periode?.gyldigFra))
      .filter(Boolean)
      .sort();
    if (dates.length === 0) continue;
    mergers.push({ unit, date: dates[0], continues: values('udgaaende').length > 0 });
  }
  return mergers;
}

/**
 * The merger boxes of one date: one box for each merger unit. `stopping` and
 * `continuing` hold `{ name, self }` for this company, `{ name, who }` for another
 * company, and `{ name: 'Ukendt selskab', unknown }` when no company is known.
 * `partnerCvr` is set when the merger has exactly one other company.
 */
export function mergerBoxes(company, events) {
  const self = { name: companyName(company) || String(company?.enhedsNummer ?? ''), self: true };
  const units = new Map();
  for (const event of list(events).filter((e) => e?.kind === 'merger')) {
    if (!units.has(event.unit)) units.set(event.unit, []);
    units.get(event.unit).push(event);
  }
  return [...units.entries()].map(([unit, unitEvents]) => {
    const continues = unitEvents[0].continues;
    const partners = unitEvents.filter((e) => e.who);
    const stopping = continues ? [] : [self];
    const continuing = continues ? [self] : [];
    for (const e of partners) (e.partnerContinues ? continuing : stopping).push({ name: e.who.name, who: e.who });
    const unknown = { name: 'Ukendt selskab', unknown: true };
    if (stopping.length === 0) stopping.push(unknown);
    if (continuing.length === 0) continuing.push(unknown);
    return {
      unit,
      continues,
      stopping,
      continuing,
      found: partners.length > 0,
      partnerCvr: partners.length === 1 ? partners[0].partnerCvr : null,
    };
  });
}

/**
 * The history of a company: `[{ date, events }]`, newest date first. An event has a
 * `kind`, a `text`, and for an owner, a role or a merger also `who` and `action`. A
 * capital event has `from` and `to` in kroner. `partners` are other companies with
 * `fusioner`: a partner with the same merger unit is the other side of that merger.
 */
export function companyHistory(company, partners = []) {
  if (!company) return [];
  const events = [];
  const add = (date, event) => events.push({ date, seq: events.length, ...event });

  const names = periodsOf(company.navne, (v) => v?.navn);
  const founded = day(company.stiftelsesDato) ?? names[0]?.from ?? null;
  if (founded && names.length > 0) add(founded, { kind: 'founded', text: `Stiftet som ${names[0].value}` });

  // A company value: no event on the founding date, the founding text holds it.
  const later = (change) => change.after !== null && change.date !== founded;
  for (const c of changesOf(names).filter((c) => later(c) && c.before !== null)) {
    add(c.date, { kind: 'name', text: `Navn ændret fra ${c.before} til ${c.after}` });
  }
  for (const c of changesOf(periodsOf(company.virksomhedsform, (v) => v?.kortBeskrivelse)).filter((c) => later(c) && c.before !== null)) {
    add(c.date, { kind: 'form', text: `Selskabsform ændret fra ${c.before} til ${c.after}` });
  }
  for (const c of changesOf(periodsOf(company.virksomhedsstatus, (v) => v?.status)).filter((c) => later(c) && c.before !== null)) {
    add(c.date, { kind: 'status', text: `Status ændret fra ${c.before} til ${c.after}` });
  }
  for (const c of changesOf(periodsOf(company.beliggenhedsadresse, (v) => formatAddress(v))).filter(later)) {
    add(c.date, { kind: 'address', text: `Adresse ændret til ${c.after}` });
  }
  for (const c of changesOf(periodsOf(company.hovedbranche, (v) => v?.branchetekst)).filter(later)) {
    add(c.date, { kind: 'industry', text: `Branche ændret til ${c.after}` });
  }

  const self = Number(company.enhedsNummer);
  for (const merger of mergersOf(company)) {
    const others = list(partners)
      .filter((partner) => Number(partner?.enhedsNummer) !== self)
      .flatMap((partner) => mergersOf(partner)
        .filter((m) => m.unit === merger.unit)
        .slice(0, 1)
        .map((m) => ({ partner, continues: m.continues })));
    const facts = { unit: merger.unit, continues: merger.continues };
    if (others.length === 0) {
      const text = merger.continues ? 'Fusion: et andet selskab indgår i selskabet' : 'Selskabet ophører ved fusion';
      add(merger.date, { kind: 'merger', ...facts, partnerContinues: null, partnerCvr: null, text });
      continue;
    }
    for (const { partner, continues } of others) {
      const partnerCvr = Number.isFinite(Number(partner.cvr)) && partner.cvr !== null ? Number(partner.cvr) : null;
      const who = { name: companyName(partner) || String(partner.enhedsNummer), enhedsNummer: Number(partner.enhedsNummer), cvr: partnerCvr, kind: 'company' };
      let action = 'indgår i selskabet ved fusion';
      if (continues) action = 'overtager selskabet ved fusion';
      else if (!merger.continues) action = 'ophører ved den samme fusion';
      add(merger.date, { kind: 'merger', ...facts, partnerContinues: continues, partnerCvr, who, action, text: `${who.name} ${action}` });
    }
  }

  const attribute = (type) => list(company.attributter).find((a) => a?.type === type)?.vaerdier;
  const capital = periodsOf(attribute('KAPITAL'), (v) => (Number.isFinite(Number(v?.vaerdi)) ? Number(v.vaerdi) : null));
  for (const c of changesOf(capital).filter((c) => c.after !== null)) {
    if (c.before === null) {
      add(c.date, { kind: 'capital', from: null, to: c.after, text: `Kapital ${kroner(c.after)}` });
    } else {
      const verb = c.after > c.before ? 'forhøjet' : 'nedsat';
      add(c.date, { kind: 'capital', from: c.before, to: c.after, text: `Kapitalen ${verb} fra ${kroner(c.before)} til ${kroner(c.after)}` });
    }
  }
  for (const c of changesOf(periodsOf(attribute('VEDTÆGT_SENESTE'), (v) => v?.vaerdi)).filter(later)) {
    add(c.date, { kind: 'articles', text: 'Nye vedtægter' });
  }

  for (const rel of list(company.deltagerRelation)) {
    const deltager = rel?.deltager;
    if (!deltager) continue;
    const who = { name: companyName(deltager) || String(deltager.enhedsNummer ?? ''), enhedsNummer: Number(deltager.enhedsNummer), cvr: participantCvr(deltager), kind: kindOf(deltager) };
    const say = (date, kind, action) => add(date, { kind, who, action, text: `${who.name} ${action}` });

    for (const org of list(rel.organisationer)) {
      const orgFunction = list(org?.attributter).find((a) => a?.type === 'FUNKTION');
      const ownerRegister = org?.hovedtype === 'REGISTER' && orgFunction?.vaerdier?.[0]?.vaerdi === OWNER_REGISTER;
      for (const member of list(org?.medlemsData)) {
        for (const att of list(member?.attributter)) {
          if (att?.type === 'EJERANDEL_PROCENT' && ownerRegister) {
            for (const c of changesOf(periodsOf(att.vaerdier, (v) => Number(v?.vaerdi)))) {
              if (c.before === null) say(c.date, 'owner', `bliver ejer (${shareRange(c.after)})`);
              else if (c.after === null) say(c.date, 'owner', 'er ikke længere ejer');
              else say(c.date, 'owner', `ejerandel ændret fra ${shareRange(c.before)} til ${shareRange(c.after)}`);
            }
          } else if (att?.type === 'FUNKTION') {
            const roles = [...new Set(list(att.vaerdier).map((v) => v?.vaerdi).filter(Boolean))];
            for (const role of roles) {
              const values = list(att.vaerdier).filter((v) => v?.vaerdi === role);
              for (const c of changesOf(periodsOf(values, () => role))) {
                if (role === 'STIFTERE') {
                  if (c.before === null && c.after !== null) say(c.date, 'role', 'er stifter');
                } else if (c.after === null) {
                  say(c.date, 'role', `stopper som ${roleName(role)}`);
                } else if (c.before === null) {
                  say(c.date, 'role', `bliver ${roleName(role)}`);
                }
              }
            }
          }
        }
      }
    }
  }

  const groups = new Map();
  for (const event of events) {
    if (!groups.has(event.date)) groups.set(event.date, []);
    groups.get(event.date).push(event);
  }
  return [...groups.entries()]
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([date, list]) => ({
      date,
      events: list
        .sort((a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind) || a.seq - b.seq)
        .map(({ seq, date: _date, ...event }) => event),
    }));
}

/** The owners that joined within `days` of `date`: the investors of a capital increase. */
export function ownersNear(groups, date, days = 14) {
  return groups
    .filter((group) => daysBetween(group.date, date) <= days)
    .flatMap((group) => group.events)
    .filter((event) => event.kind === 'owner' && event.action.startsWith('bliver ejer'));
}

/** The fiscal year `{ start, end }` that holds `date`, or null. */
export function reportFor(date, years) {
  return (years ?? []).find((y) => y?.start && y?.end && y.start <= date && date <= y.end) ?? null;
}

/** True when `date` is after the end of every loaded report: its report is not published yet. */
export function reportPending(date, years) {
  const ends = (years ?? []).map((y) => y?.end).filter(Boolean);
  return ends.length > 0 && ends.every((end) => end < date);
}

// ── XBRL ─────────────────────────────────────────────────────────────
//
// cvrfinancial.xml is the XBRL of the report, converted to JSON (`_attributes`, `_text`).
// The prefixes are the filer's choice. One report has `xbrli:xbrl`, `xbrli:context` and
// `fsa:SharePremium`; another has the instance namespace as the default (`xbrl`,
// `context`) and fsa as `e:` or `g:`. So the prefixes come from the `xmlns` attributes.
//
// A context with a scenario or a segment is one part of the company. In the equity
// statement that is a column: ContributedCapitalMember, SharePremiumMember,
// RetainedEarningsMember.

function prefixesOf(attributes, namespace) {
  return Object.keys(attributes)
    .filter((key) => (key === 'xmlns' || key.startsWith('xmlns:')) && attributes[key] === namespace)
    .map((key) => (key === 'xmlns' ? '' : key.slice('xmlns:'.length)));
}

function qualified(prefix, local) {
  return prefix ? `${prefix}:${local}` : local;
}

function localName(text) {
  const name = String(text ?? '').trim();
  return name.includes(':') ? name.slice(name.indexOf(':') + 1) : name;
}

// The column of a dimension context: the local name of its explicit member, or null.
function columnOf(scenario) {
  for (const [key, value] of Object.entries(scenario ?? {})) {
    if (key !== 'explicitMember' && !key.endsWith(':explicitMember')) continue;
    const members = list(value).map((m) => localName(m?._text)).filter(Boolean);
    if (members.length === 1) return members[0];
  }
  return null;
}

// { contexts: Map(id -> {instant, start, end, dimensional, column}), facts(concept) }, or null.
function readXbrl(xml) {
  if (!xml || typeof xml !== 'object') return null;
  const rootKey = Object.keys(xml).find((key) => key === 'xbrl' || key.endsWith(':xbrl'));
  const root = rootKey ? xml[rootKey] : null;
  if (!root || typeof root !== 'object') return null;
  const attributes = root._attributes ?? {};

  const rootPrefix = rootKey.includes(':') ? rootKey.slice(0, rootKey.indexOf(':')) : '';
  const instance = [rootPrefix, ...prefixesOf(attributes, INSTANCE_NAMESPACE)]
    .find((prefix) => root[qualified(prefix, 'context')] !== undefined) ?? rootPrefix;
  const fsa = prefixesOf(attributes, FSA_NAMESPACE);
  if (fsa.length === 0) fsa.push('fsa');

  const contexts = new Map();
  for (const context of list(root[qualified(instance, 'context')])) {
    const id = context?._attributes?.id;
    if (!id) continue;
    const period = context[qualified(instance, 'period')] ?? {};
    const text = (local) => day(period[qualified(instance, local)]?._text);
    const part = context[qualified(instance, 'scenario')] ?? context[qualified(instance, 'entity')]?.[qualified(instance, 'segment')];
    contexts.set(id, {
      instant: text('instant'),
      start: text('startDate'),
      end: text('endDate'),
      dimensional: Boolean(part),
      column: part ? columnOf(part) : null,
    });
  }
  const facts = (concept) => fsa.flatMap((prefix) => list(root[qualified(prefix, concept)]))
    .map((fact) => ({ context: contexts.get(fact?._attributes?.contextRef), value: Number(fact?._text) }))
    .filter((fact) => fact.context && Number.isFinite(fact.value));
  return { facts };
}

/** The value of an fsa concept (for example "SharePremium") on a balance date, for the whole company. */
export function xbrlInstant(xml, concept, date) {
  const xbrl = readXbrl(xml);
  const fact = xbrl?.facts(concept).find((f) => !f.context.dimensional && f.context.instant === date);
  return fact ? fact.value : null;
}

/**
 * An fsa concept for the fiscal year `start`..`end`: `{ plain, columns }`, or null when
 * the report has no such fact. `plain` is the fact without a column; `columns` holds the
 * facts of the equity statement by column.
 */
export function xbrlYearColumns(xml, concept, start, end) {
  const xbrl = readXbrl(xml);
  if (!xbrl) return null;
  let found = false;
  let plain = null;
  const columns = {};
  for (const fact of xbrl.facts(concept)) {
    if (fact.context.start !== start || fact.context.end !== end) continue;
    if (!fact.context.dimensional) {
      plain = fact.value;
      found = true;
    } else if (fact.context.column) {
      columns[fact.context.column] = (columns[fact.context.column] ?? 0) + fact.value;
      found = true;
    }
  }
  return found ? { plain, columns } : null;
}

function change(close, open) {
  return close === null || open === null ? null : close - open;
}

/**
 * The money of a capital increase `{ date, from, to }`. `report` is the annual report
 * `{ start, end, xml }` of its fiscal year, and `shared` is the number of increases in
 * that year. An amount that the report does not show is null.
 *
 * The capital part, first found wins:
 *   1. IncreaseOfCapital in the capital column of the equity statement;
 *   2. the growth of ContributedCapital in the balance;
 *   3. the CVR increase (only when it is the one increase of the year).
 * The premium part, first found wins (`source`):
 *   1. 'equity': IncreaseOfCapital in the premium column;
 *   2. 'balance' / 'transfer': the new premium of the year from the balance, that is the
 *      share premium at the end, minus at the start, plus the premium that moved to
 *      retained earnings in the year.
 * A filer can write a negative sign in the equity statement (4008858541), so a column
 * counts as its size. The retained earnings column never counts as money paid in: one
 * filer put 32 mio. there that is no increase (4006315559, 2018).
 */
export function investment(increase, report, shared = 1) {
  const nominal = increase.to - increase.from;
  const newShare = increase.to > 0 ? nominal / increase.to : null;
  let capital = shared === 1 ? nominal : null;
  let premium = null;
  let source = null;

  if (report?.xml && report.start && report.end) {
    const opening = addDays(report.start, -1);
    const increases = xbrlYearColumns(report.xml, 'IncreaseOfCapital', report.start, report.end);
    const capitalColumn = Math.abs(increases?.columns.ContributedCapitalMember ?? 0);
    const premiumColumn = Math.abs(increases?.columns.SharePremiumMember ?? 0);
    const capitalGrowth = change(
      xbrlInstant(report.xml, 'ContributedCapital', report.end),
      xbrlInstant(report.xml, 'ContributedCapital', opening),
    );
    if (capitalColumn > 0) capital = capitalColumn;
    else if (capitalGrowth > 0) capital = capitalGrowth;

    if (premiumColumn > 0) {
      premium = premiumColumn;
      source = 'equity';
    } else {
      const close = xbrlInstant(report.xml, 'SharePremium', report.end) ?? 0;
      const open = xbrlInstant(report.xml, 'SharePremium', opening) ?? 0;
      const moved = xbrlYearColumns(report.xml, 'TransferredFromSharePremium', report.start, report.end);
      const movedAmount = Math.abs(moved?.columns.SharePremiumMember ?? moved?.columns.RetainedEarningsMember ?? moved?.plain ?? 0);
      const fresh = close - open + movedAmount;
      if (fresh > 0) {
        premium = fresh;
        source = movedAmount > 0 ? 'transfer' : 'balance';
      }
    }
  }

  const paid = premium === null || capital === null ? null : capital + premium;
  let reportLabel = null;
  if (report?.start && report?.end) {
    const [first, last] = [report.start.slice(0, 4), report.end.slice(0, 4)];
    reportLabel = first === last ? first : `${first}/${last.slice(2)}`;
  }
  return {
    nominal,
    capital,
    premium,
    paid,
    newShare,
    pricePerKrone: paid === null || !(capital > 0) ? null : paid / capital,
    valuation: paid === null || shared !== 1 || !newShare ? null : paid / newShare,
    reportLabel,
    shared,
    source,
  };
}
